import { AbstractControl, FormGroup, NgForm, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Exige que el valor sea un número en incrementos de media hora (1, 1.5, 2,
 * 2.5...) -- usado en las tarifas escalonadas de un espacio (min_hours/
 * max_hours). Mismo criterio que valida el backend (SpacePricingTier.ts y
 * spacePricingTier.controllers.ts): rechaza 2.3 o 3.4, acepta X.0 y X.5.
 * Redondear a décimas antes del módulo evita falsos negativos por
 * imprecisión de punto flotante (2.3*10 puede dar 22.999999999997 en JS).
 */
export const halfHourIncrementValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  const tenths = Math.round(num * 10);
  return tenths % 5 === 0 ? null : { halfHourIncrement: true };
};

/**
 * Nombre del primer control de nivel superior que esté inválido, en el orden
 * en que se declararon en el FormGroup. Se usa en formularios divididos en
 * pestañas: antes de invocar validateAndFocusFirstError hay que cambiar a la
 * pestaña que contiene ese campo, porque el resaltado busca el elemento en el
 * DOM y las pestañas inactivas no deben eliminar sus campos del DOM (deben
 * ocultarse con [hidden]/CSS, nunca con *ngIf) para que la búsqueda los
 * encuentre igual.
 */
export function firstInvalidControlName(form: FormGroup): string | null {
  for (const name of Object.keys(form.controls)) {
    if (form.get(name)?.invalid) return name;
  }
  return null;
}

/**
 * Los botones de "crear"/"guardar" nunca deben deshabilitarse por invalidez del
 * formulario (solo mientras la acción está en curso). En su lugar, esta función
 * se llama al hacer click: si el formulario es válido, deja continuar (true).
 * Si no, marca todos los campos como tocados (para que aparezcan los mensajes
 * de error por campo ya existentes), notifica (vía el mecanismo propio del
 * componente, sea NotificationService o un toast local), y desplaza/resalta
 * el primer campo inválido dentro del contenedor indicado.
 */
// Busca `selector` dentro de `root` reintentando en varios frames (Angular
// puede tardar más de un ciclo de detección de cambios en pintar las clases
// ng-invalid/ng-touched tras markAllAsTouched, sobre todo en formularios
// grandes, o en registrar un control recién insertado por un *ngIf) y, al
// encontrarlo, hace scroll suave hasta él, lo resalta con un destello
// temporal y le da foco. Compartido por validateAndFocusFirstError (campos
// que Angular ya marca inválidos) y focusFieldWithError (reglas de negocio
// manuales, ver más abajo) para que AMBOS casos lleven al usuario al campo
// exacto en vez de solo mostrar un aviso genérico. Se probaron varios
// selectores en orden (`selectors`, no uno solo) porque el primero que
// encuentre resultado gana -- así se puede intentar primero un selector
// puntual (p. ej. por nombre de campo) y caer a uno más genérico si no
// aparece nada.
function scrollToAndFlashField(root: HTMLElement, selectors: string[], attemptsLeft = 20): void {
  let target: HTMLElement | null = null;
  for (const selector of selectors) {
    target = root.querySelector<HTMLElement>(selector);
    if (target) break;
  }
  if (!target) {
    if (attemptsLeft > 0) requestAnimationFrame(() => scrollToAndFlashField(root, selectors, attemptsLeft - 1));
    return;
  }
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  target.classList.add('field-error-flash');
  target.focus({ preventScroll: true });
  setTimeout(() => target!.classList.remove('field-error-flash'), 1900);
}

// Mensajes escritos a mano en las plantillas (o el ya generado). Los
// generados también llevan .field-validation-message, por eso ese caso se
// excluye con :not([data-generated-field-error]) y se cuenta aparte.
const INLINE_ERROR_SELECTOR = [
  '.error', '.error-text', '.error-msg', '.error-message', '.field-error', '.password-error',
  '.validation-error', '.invalid-feedback', '.field-validation-message:not([data-generated-field-error])',
  '[data-generated-field-error]',
].join(', ');

function readableFieldName(root: HTMLElement, control: HTMLElement, name: string): string {
  const id = control.getAttribute('id');
  const label = id ? root.querySelector<HTMLLabelElement>(`label[for="${id}"]`) : null;
  return String(label?.textContent || control.getAttribute('aria-label') || control.getAttribute('placeholder') || name)
    .replace(/\s*\*\s*$/, '')
    .trim()
    .toLocaleLowerCase('es');
}

function validationMessage(label: string, errors: ValidationErrors | null): string {
  if (!errors) return `Revisa el valor ingresado en ${label}.`;
  if (errors['required']) return `El campo ${label} es obligatorio.`;
  if (errors['email']) return `Ingresa un correo electrónico válido en ${label}.`;
  if (errors['minlength']) return `${label} debe tener al menos ${errors['minlength'].requiredLength} caracteres.`;
  if (errors['maxlength']) return `${label} no puede superar ${errors['maxlength'].requiredLength} caracteres.`;
  if (errors['min']) return `${label} debe ser mayor o igual a ${errors['min'].min}.`;
  if (errors['max']) return `${label} debe ser menor o igual a ${errors['max'].max}.`;
  if (errors['pattern']) return `El formato ingresado en ${label} no es válido.`;
  if (errors['passwordMismatch']) return 'Las contraseñas deben coincidir.';
  if (errors['halfHourIncrement']) return `${label} debe usar intervalos de media hora, por ejemplo 1, 1.5 o 2.`;
  return `Revisa el valor ingresado en ${label}.`;
}

/**
 * Respaldo transversal para formularios que todavía no tienen un mensaje
 * específico en su plantilla. No reemplaza los mensajes escritos a mano:
 * solamente crea uno cuando el campo inválido quedaría señalado únicamente
 * por color o por una notificación general.
 */
function ensureInlineValidationMessages(formGroup: FormGroup, root: HTMLElement): void {
  root.querySelectorAll('[data-generated-field-error]').forEach(node => node.remove());

  for (const [name, abstractControl] of Object.entries(formGroup.controls)) {
    if (!abstractControl.invalid) continue;
    const field = root.querySelector<HTMLElement>(`[formControlName="${name}"], [name="${name}"]`);
    if (!field) continue;

    field.setAttribute('aria-invalid', 'true');
    const container = field.closest<HTMLElement>('.form-group, .field, .input-group, .control-group') || field.parentElement;
    if (!container || container.querySelector(INLINE_ERROR_SELECTOR)) continue;

    const message = document.createElement('small');
    const messageId = `field-error-${name}-${Math.random().toString(36).slice(2, 8)}`;
    message.id = messageId;
    message.className = 'field-validation-message';
    message.setAttribute('role', 'alert');
    message.setAttribute('aria-live', 'assertive');
    message.setAttribute('data-generated-field-error', name);
    message.textContent = validationMessage(readableFieldName(root, field, name), abstractControl.errors);
    field.insertAdjacentElement('afterend', message);
    field.setAttribute('aria-describedby', [field.getAttribute('aria-describedby'), messageId].filter(Boolean).join(' '));

    const clearWhenValid = () => setTimeout(() => {
      if (!field.classList.contains('ng-invalid')) {
        message.remove();
        field.removeAttribute('aria-invalid');
        const describedBy = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(id => id && id !== messageId);
        describedBy.length ? field.setAttribute('aria-describedby', describedBy.join(' ')) : field.removeAttribute('aria-describedby');
        field.removeEventListener('input', clearWhenValid);
        field.removeEventListener('change', clearWhenValid);
      }
    });
    field.addEventListener('input', clearWhenValid);
    field.addEventListener('change', clearWhenValid);
  }
}

export function validateAndFocusFirstError(
  form: FormGroup | NgForm,
  container: HTMLElement | Element | null | undefined,
  notifyError: (message: string) => void,
  message = 'Completa correctamente los campos marcados antes de continuar.',
): boolean {
  const formGroup = form instanceof FormGroup ? form : form.form;
  if (formGroup.valid) return true;

  formGroup.markAllAsTouched();
  formGroup.updateValueAndValidity();
  notifyError(message);
  // Quita el foco de donde estuviera antes de reasignarlo — evita que el
  // anillo de :focus de un campo válido (p. ej. el último que el usuario
  // editó) se confunda visualmente con el resaltado de error mientras se
  // localiza el campo realmente inválido.
  (document.activeElement as HTMLElement | null)?.blur?.();

  const root = container as HTMLElement | null;
  if (root) {
    // Los mensajes propios de cada plantilla suelen depender de
    // `touched` (*ngIf="...touched"), y markAllAsTouched no los pinta
    // hasta el siguiente ciclo de detección de cambios. Si el respaldo se
    // generara aquí mismo, todavía no los encontraría y el campo acabaría
    // con DOS mensajes (el propio + el genérico). Por eso se espera a que
    // Angular renderice (timeout + frame) antes de decidir.
    root.querySelectorAll('[data-generated-field-error]').forEach(node => node.remove());
    setTimeout(() => requestAnimationFrame(() => ensureInlineValidationMessages(formGroup, root)));
    // Se identifica el control inválido por NOMBRE (vía el propio FormGroup
    // -- no depende de que Angular ya haya pintado ng-invalid en el DOM,
    // que es una condición de carrera en formularios grandes o con
    // secciones que aparecen por *ngIf) y se busca [name="ese-nombre"] en
    // el DOM. Si por algún motivo esa búsqueda puntual no encuentra nada
    // (p. ej. un grupo anidado sin campo propio), se cae al selector
    // genérico por clase como respaldo, nunca se deja de intentar.
    const invalidName = firstInvalidControlName(formGroup);
    const selectors = [
      ...(invalidName ? [`[name="${invalidName}"]`] : []),
      'input.ng-invalid, textarea.ng-invalid, select.ng-invalid, .ng-invalid input, .ng-invalid textarea, .ng-invalid select',
    ];
    setTimeout(() => scrollToAndFlashField(root, selectors), 30);
  }

  return false;
}

/**
 * Para validaciones MANUALES que no pasan por el FormGroup de Angular (p.
 * ej. "la hora de fin debe ser posterior a la de inicio", "las personas
 * superan la capacidad del espacio") -- antes estos casos solo mostraban un
 * toast y dejaban al usuario buscando a mano en formularios largos qué
 * campo corregir. `fieldSelector` debe apuntar al control específico
 * responsable del error (p. ej. `[name="editEnd"]`) dentro de `container`.
 */
export function focusFieldWithError(
  container: HTMLElement | Element | null | undefined,
  fieldSelector: string,
  notifyError: (message: string) => void,
  message: string,
): void {
  notifyError(message);
  (document.activeElement as HTMLElement | null)?.blur?.();
  const root = container as HTMLElement | null;
  if (root) {
    setTimeout(() => scrollToAndFlashField(root, [fieldSelector]), 30);
  }
}
